"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type PropsWithChildren,
} from "react";

import { isSupportedPoolNetwork } from "@/feature/liquidity-pools/supported-networks";
import { getPoolAnalysisUrlPreservingParams } from "@/feature/liquidity-pools/utils/uniswap";

interface AnalysisSidebarState {
	pool: string;
	network: string;
}

interface AnalysisSidebarContextValue {
	openPool: AnalysisSidebarState | null;
	openAnalysis: (pool: string, network: string) => void;
	closeAnalysis: () => void;
}

const AnalysisSidebarContext = createContext<AnalysisSidebarContextValue | null>(null);

export function AnalysisSidebarProvider({ children }: PropsWithChildren) {
	const urlBeforeOpen = useRef<string | null>(null);
	const [openPool, setOpenPool] = useState<AnalysisSidebarState | null>(null);

	const openAnalysis = useCallback((pool: string, network: string) => {
		if (!isSupportedPoolNetwork(network)) return;

		setOpenPool({ pool, network });

		if (window.location.pathname === "/pools") {
			urlBeforeOpen.current = window.location.href;
			window.history.pushState(
				null,
				"",
				getPoolAnalysisUrlPreservingParams(network, pool, window.location.href),
			);
		}
	}, []);

	const closeAnalysis = useCallback(() => {
		setOpenPool(null);

		if (urlBeforeOpen.current) {
			const restoreUrl = urlBeforeOpen.current ?? "/pools";
			urlBeforeOpen.current = null;
			window.history.replaceState(null, "", restoreUrl);
		}
	}, []);

	useEffect(() => {
		const syncFromUrl = () => {
			const url = new URL(window.location.href);
			const pool = url.searchParams.get("analysisPool");
			const network = url.searchParams.get("analysisNetwork");

			if (pool && network) {
				if (!isSupportedPoolNetwork(network)) {
					url.searchParams.delete("analysisPool");
					url.searchParams.delete("analysisNetwork");
					const qs = url.searchParams.toString();
					window.history.replaceState(null, "", qs ? `${url.pathname}?${qs}` : url.pathname);
					setOpenPool(null);
					return;
				}
				setOpenPool({ pool, network });
				return;
			}

			if (urlBeforeOpen.current) {
				setOpenPool(null);
				urlBeforeOpen.current = null;
			}
		};

		syncFromUrl();
		const onPopState = () => syncFromUrl();
		window.addEventListener("popstate", onPopState);
		return () => window.removeEventListener("popstate", onPopState);
	}, []);

	return (
		<AnalysisSidebarContext.Provider value={{ openPool, openAnalysis, closeAnalysis }}>
			{children}
		</AnalysisSidebarContext.Provider>
	);
}

export function useAnalysisSidebar() {
	const ctx = useContext(AnalysisSidebarContext);
	if (!ctx) throw new Error("useAnalysisSidebar must be used within AnalysisSidebarProvider");
	return ctx;
}

export function useAnalysisSidebarState() {
	const ctx = useContext(AnalysisSidebarContext);
	return ctx?.openPool ?? null;
}
